// ConfirmDialog — PA3 viewport-aware confirmation surface.
//
// Per `component-api.md §3 PA3`:
//   - Desktop-modal: 440px centered, Radix Dialog with backdrop
//   - Mobile: auto-promote to BottomSheet variant="mobile" (90vh slide-up)
//   - 「撕信」 animation 仅 destructive 操作 (desktop only — mobile uses
//     sheet slide-down for dismiss)
//
// W34.3.1: auto-inheritance via `useSheetVariant`. All existing callers
// (DiaryDetail delete, the 5 wishlist hotspots shipped in W34.3.2) keep the
// same `<ConfirmDialog ...>` API;
// the mobile BottomSheet variant is selected inside the component. No
// caller needs to branch on viewport.
//
// W56.0 J (ADR-0201): `confirmLabel` is **required**. It used to default to
// 「確認」 — a label that names no action, inherited by every call site that
// never chose one. Soft deletes no longer come through here at all (they go
// straight to the `UndoBar`); what is left are the genuinely irreversible
// operations, and those are exactly the ones whose button must say what it
// does.
//
// Anti-drift guards:
//   - 不新做 BottomSheet variant — reuse variant="mobile" + "desktop-modal"
//   - 不在 mobile 觸發 撕信 overlay — viewport 太擠
//   - 不要 hardcode 視口寬度 — useIsMobile() 內部 mql 處理
//   - 不要把 ConfirmDialog 改名 — 既存呼叫點不應改 import

import { BottomSheet } from "./BottomSheet";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils/cn";
import { useSheetVariant } from "@/lib/hooks/useSheetVariant";

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  /** When true, applies 「撕信」 animation + warning tone (per spec). */
  destructive?: boolean;
  /**
   * The confirming button's label. **Required** (W56.0 J, ADR-0201).
   *
   * It used to default to 「確認」 — and a default that vague is a default
   * every future call site inherits without deciding anything. 「確認」 tells
   * the reader nothing about what they are agreeing to; the label has to name
   * the action. Making it required moves that decision from "nobody" to
   * "the call site", and TypeScript is what enforces it.
   */
  confirmLabel: string;
  cancelLabel?: string;
  onConfirm: () => void;
}

const DESKTOP_WIDTH = 440;

/**
 * PA3 — ConfirmDialog.
 *
 * Per `component-api.md §3 PA3`:
 *   - Desktop-modal (440px centered)
 *   - Mobile auto-promotes to BottomSheet variant="mobile" (90vh)
 *   - 「撕信」 animation 仅 destructive 操作 (desktop only)
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  destructive = false,
  confirmLabel,
  cancelLabel = "取消",
  onConfirm,
}: ConfirmDialogProps) {
  const { variant, isMobile } = useSheetVariant({
    desktopWidth: DESKTOP_WIDTH,
  });

  // W34.3.1 anti-drift: 撕信 overlay only on desktop. On mobile the sheet
  // slide-down handles dismiss; viewport is too cramped for the two
  // diagonal halves to read.
  const showTear = destructive && !isMobile;

  return (
    <div
      data-component="ConfirmDialog"
      data-variant={isMobile ? "mobile" : "desktop"}
      data-destructive={destructive ? "true" : undefined}
    >
      <BottomSheet
        open={open}
        onOpenChange={onOpenChange}
        variant={variant}
        title={title}
        description={description}
        // Override BottomSheet's default 520px width on desktop so the
        // ConfirmDialog matches the v1 440px centered modal. Mobile
        // ignores className — BottomSheet's mobile variant is full-width.
        className={cn(
          !isMobile && "!w-[440px]",
          destructive && !isMobile && "border-partner/30",
        )}
      >
        {showTear ? (
          // Desktop destructive 撕信 overlay (CSS keyframes per
          // docs/spec/DESIGN.md §9 pattern 3). Two diagonal halves
          // slide apart on confirm.
          <div
            data-testid="confirm-dialog-tear"
            className="pointer-events-none absolute inset-0 overflow-hidden rounded-lg"
          >
            <div className="absolute inset-y-0 left-0 w-1/2 bg-paper origin-right animate-tear-left" />
            <div className="absolute inset-y-0 right-0 w-1/2 bg-paper origin-left animate-tear-right" />
          </div>
        ) : null}
        <div
          data-component="ConfirmDialogActions"
          className={cn(
            // Desktop: right-aligned horizontal layout (per W34.3.1
            // spec).
            !isMobile && "flex justify-end gap-2",
            // Mobile: full-width stacked (cancel on top, confirm on
            // bottom) per Apple HIG + 撕信 spec — destructive confirm
            // uses partner-strong bg.
            isMobile && "flex flex-col gap-s-2",
          )}
        >
          <Button
            type="button"
            variant="secondary"
            onClick={() => onOpenChange(false)}
            className={cn(isMobile && "w-full min-h-12")}
          >
            {cancelLabel}
          </Button>
          <Button
            type="button"
            variant={destructive ? "destructive" : "primary"}
            onClick={onConfirm}
            className={cn(isMobile && "w-full min-h-12")}
          >
            {confirmLabel}
          </Button>
        </div>
      </BottomSheet>
    </div>
  );
}